import { zodResolver } from "@hookform/resolvers/zod";
import { isLoading } from "apollo/utils";
import clsx from "clsx";
import {
  FILE_CATEGORIES,
  FILE_TABLE_ACTION_UPLOAD_DRAGNDROP,
} from "core/consts";
import { useIsDocsAndExtractActive } from "core/model/careseekers";
import { ONTOLOGIES } from "core/model/utils/ontologies";
import { useGetOntologiesForSelect } from "core/model/utils/ontologies/hooks";
import { preprocessToOptionalNumber } from "core/schemas/utils";
import {
  FileCategory,
  FileScope,
  FileShareMode,
  Filev2,
  QueryProgress,
  RecareFileType,
} from "core/types";
import {
  Button,
  ControlledInput,
  ControlledSelect,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Spinner,
} from "ds/ui";
import { SetAction } from "ds_legacy/components/Tables/GeneralTable/MenuActions";
import { FileFeatureData } from "dsl/ecosystems/PatientFiles/useFilesFeature";
import { useIsEligiblePage } from "dsl/ecosystems/PatientFiles/useUploadFile";
import { useIsScrollable } from "dsl/hooks";
import { EncryptedFileMessageTailwind } from "dsl/organisms/FileTables";
import { ArrowLeftIcon, LockIcon, UploadIcon, XIcon } from "lucide-react";
import { type RefObject, useCallback, useEffect, useMemo, useRef } from "react";
import {
  FieldArrayWithId,
  FormProvider,
  useFieldArray,
  useForm,
  useFormContext,
  useWatch,
} from "react-hook-form";
import { useTranslations } from "translations";
import Translations from "translations/types";
import { z } from "zod";
import { ProcessedFile } from "..";
import { createClientId } from "../createClientId";
import type { SavedUploadFormDraft } from "../savedUploadFormDraft";
import { ExtractBanner, getFileExtension, getFileStem } from "./utils";

export type FileInitialValues = {
  category?: { id: number; label: string; value: FileCategory } | null;
  fileName: string;
  kis_document_id_string: Filev2["kis_document_id_string"] | undefined;
  should_extract_on_upload: boolean;
} & ProcessedFile;

type FileSubmittedValues = {
  form_synced_with_file?: boolean;
  category_other?: string;
  category: FileCategory;
  fileName: string;
  kis_document_id_string: Filev2["kis_document_id_string"] | undefined;
  recare_file_type?: RecareFileType;
  share_mode?: FileShareMode;
  file_scope?: FileScope;
  should_extract_on_upload?: boolean;
};

export type UploadedFileResult = FileSubmittedValues & ProcessedFile;

export type FileUploadInitialValues = FileInitialValues;

export type FileUploadModalProps = {
  handleRemoveFile: (index: number) => void;
  onSubmit: (data: UploadedFileResult[]) => Promise<void>;
  initialValues: FileUploadInitialValues[];
  fileUploadProgress: QueryProgress;
  mutationProgress: QueryProgress;
  savedFormDataRef: RefObject<SavedUploadFormDraft>;
  setAction: SetAction<FileFeatureData>;
};

type FileFieldRow = FormValues["files"][number] & {
  content?: string;
  file?: File;
  id?: string;
};

const fileUploadFormSchema = (translations: Translations) =>
  z.object({
    files: z.array(
      z
        .object({
          category: z.preprocess<
            number | undefined,
            z.ZodNumber,
            FileCategory | undefined
          >(
            (val) => {
              if (Array.isArray(val)) {
                const firstItem = val.length > 0 ? val[0] : undefined;
                return preprocessToOptionalNumber(firstItem);
              }
              return preprocessToOptionalNumber(val);
            },
            z
              .number({
                error: translations.fileUploader.newModal.errorRequiredCategory,
              })
              .refine((val): val is FileCategory =>
                Object.values(FILE_CATEGORIES).includes(val as FileCategory),
              ),
          ),
          fileNameExtension: z.string(),
          fileNameStem: z
            .string()
            .refine(
              (val) => val.length > 0,
              translations.fileUploader.newModal.errorRequiredFileName,
            ),
        })
        .transform(({ fileNameExtension, fileNameStem, ...rest }) => ({
          ...rest,
          fileName: fileNameStem + fileNameExtension,
        })),
    ),
    should_extract_on_upload: z.boolean(),
  });

type FormValues = z.input<ReturnType<typeof fileUploadFormSchema>>;
type FormValuesParsed = z.infer<ReturnType<typeof fileUploadFormSchema>>;

function buildInitialValuesSignature(
  initialValues: FileUploadInitialValues[],
): string {
  if (!initialValues.length) {
    return "";
  }
  return `${initialValues.length}:${initialValues
    .map(
      (f) =>
        `${f.fileName ?? ""}\0${f.file?.lastModified ?? ""}\0${
          f.file?.size ?? ""
        }`,
    )
    .sort((a, b) => a.localeCompare(b))
    .join(",")}`;
}

const EMPTY_SAVED_UPLOAD_DRAFT: SavedUploadFormDraft = {
  categories: [],
  fileNames: [],
};

function useFileNameField(index: number) {
  const { control } = useFormContext<FormValues>();
  const stem = useWatch({
    control,
    defaultValue: "",
    name: `files.${index}.fileNameStem`,
  });
  const extension = useWatch({
    control,
    defaultValue: "",
    name: `files.${index}.fileNameExtension`,
  });

  return { extension: extension ?? "", stem: stem ?? "" };
}

const FileOverviewRow = ({
  index,
  onRemoveFile,
}: {
  index: number;
  onRemoveFile: (index: number) => void;
}) => {
  const translations = useTranslations();
  const { extension } = useFileNameField(index);
  const fileCategoryOntologies = useGetOntologiesForSelect({
    sort: true,
    type: ONTOLOGIES.FILE_CATEGORY,
  });

  return (
    <div className="flex flex-row gap-4">
      <div className="grow">
        <ControlledInput
          data-testid={`files.${index}.fileNameStem`}
          endContent={
            <div className="flex items-center gap-2">
              {extension && (
                <span className="text-body-md whitespace-nowrap text-gray-600">
                  {extension}
                </span>
              )}
              <LockIcon className="text-gray-600" size={16} />
            </div>
          }
          isRequired
          label={translations.fileSection.fileUploadComponent.fileName}
          labelPlacement="outside-top"
          name={`files.${index}.fileNameStem`}
          placeholder={translations.fileSection.fileUploadComponent.fileName}
        />
      </div>
      <div className="grow self-end">
        <ControlledSelect
          isRequired
          items={fileCategoryOntologies}
          label={translations.fileSection.fileUploadComponent.fileCategory}
          name={`files.${index}.category`}
          placeholder={
            translations.fileSection.fileUploadComponent.fileCategory
          }
          renderItem={(item) => item.label}
          variant="outline"
        />
      </div>
      <div className="flex h-16 items-end">
        <Button
          aria-label={translations.actions.delete}
          color="black"
          id="file-upload-modal-remove-file"
          isIconOnly
          size="sm"
          variant="light"
          onPress={() => onRemoveFile(index)}
        >
          <XIcon className="text-gray-600" size={16} />
        </Button>
      </div>
    </div>
  );
};

const FileOverview = ({
  fields,
  modalBodyRef,
  mutationProgress,
  onRemoveFile,
}: {
  fields: FieldArrayWithId<FormValues, "files", "id">[];
  modalBodyRef: RefObject<HTMLDivElement | null>;
  mutationProgress: QueryProgress;
  onRemoveFile: (index: number) => void;
}) => {
  const translations = useTranslations();

  if (isLoading(mutationProgress)) {
    return (
      <>
        <h2>{translations.general.oneMoment}</h2>
        <p>{translations.fileSection.events.documentIsBeingUploaded}</p>
      </>
    );
  }

  return (
    <div ref={modalBodyRef} className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <h2 className="text-heading-lg font-bold">
          {translations.extractDocs.fileUploadModal.instructionsCheckName}
        </h2>
        <EncryptedFileMessageTailwind />
      </div>
      {fields.map((field, index) => (
        <FileOverviewRow
          key={field.id}
          index={index}
          onRemoveFile={onRemoveFile}
        />
      ))}
    </div>
  );
};

export function FileUploadScreen({
  fileUploadProgress,
  handleRemoveFile,
  initialValues,
  mutationProgress,
  onSubmit,
  savedFormDataRef,
  setAction,
}: FileUploadModalProps) {
  const translations = useTranslations();
  const isEligiblePage = useIsEligiblePage();

  const { isDischargeLetterActive, isDocsAndExtractActive } =
    useIsDocsAndExtractActive();
  const { isScrollable, ref: modalBodyRef } = useIsScrollable<HTMLDivElement>();

  const showExtractCheckbox =
    (isDocsAndExtractActive || isDischargeLetterActive) && !isEligiblePage;

  const defaultValues = useMemo(
    () => ({
      files: initialValues.map((file, index) => ({
        category:
          (savedFormDataRef.current ?? EMPTY_SAVED_UPLOAD_DRAFT).categories[
            index
          ] ?? file.category?.value,
        content: file.content,
        file: file.file,
        fileNameExtension: getFileExtension(file.fileName ?? ""),
        fileNameStem: getFileStem(file.fileName ?? ""),
        id: createClientId(),
        kis_document_id_string: file.kis_document_id_string,
      })),
      should_extract_on_upload: showExtractCheckbox,
    }),
    [initialValues, showExtractCheckbox, savedFormDataRef],
  );

  const resolver = useMemo(
    () => zodResolver(fileUploadFormSchema(translations)),
    [translations],
  );

  const methods = useForm<FormValues, unknown, FormValuesParsed>({
    defaultValues,
    resolver,
  });

  const isRemovingRef = useRef(false);
  const lastAppliedInitialValuesSignatureRef = useRef<string | null>(null);
  const prevSyncedInitialFilesRef = useRef<(File | undefined)[]>([]);

  const { fields, remove, replace } = useFieldArray({
    control: methods.control,
    name: "files",
  });

  const shouldExtractOnUpload = useWatch({
    control: methods.control,
    name: "should_extract_on_upload",
  });

  const handleRemoveFileAtIndex = useCallback(
    (index: number) => {
      isRemovingRef.current = true;

      const currentFiles = methods.getValues().files;
      (
        savedFormDataRef as unknown as {
          current: SavedUploadFormDraft;
        }
      ).current = {
        categories: currentFiles
          .filter((_, i) => i !== index)
          .map((f) => f.category),
        fileNames: currentFiles
          .filter((_, i) => i !== index)
          .map((f) => f.fileNameStem + f.fileNameExtension),
      };

      remove(index);
      handleRemoveFile(index);

      if (fields.length === 1) {
        setAction({
          data: [] as FileFeatureData,
          type: FILE_TABLE_ACTION_UPLOAD_DRAGNDROP,
        });
      }
    },
    [
      fields.length,
      handleRemoveFile,
      methods,
      remove,
      savedFormDataRef,
      setAction,
    ],
  );

  const handleFormSubmit = useCallback(
    (data: FormValuesParsed) => {
      const filesToSubmit: UploadedFileResult[] = data.files.map(
        (fileForm, index) => {
          const initial = initialValues[index];
          if (!initial) throw new Error("Initial file data missing");
          if (!fileForm.category) throw new Error("Category is required");

          const file =
            fileForm.fileName !== initial.file.name
              ? new File([initial.file], fileForm.fileName, {
                  type: initial.file.type,
                })
              : initial.file;

          return {
            ...initial,
            category: fileForm.category as FileCategory,
            file,
            fileName: fileForm.fileName,
            should_extract_on_upload: data.should_extract_on_upload,
          };
        },
      );

      onSubmit(filesToSubmit);
    },
    [initialValues, onSubmit],
  );

  const initialValuesSignature = buildInitialValuesSignature(initialValues);

  const handlePressBack = useCallback(() => {
    const currentFiles = methods.getValues().files;
    (savedFormDataRef as unknown as { current: SavedUploadFormDraft }).current =
      {
        categories: currentFiles.map((f) => f.category),
        fileNames: currentFiles.map(
          (f) => f.fileNameStem + f.fileNameExtension,
        ),
      };
    setAction({
      data: {} as FileFeatureData,
      type: FILE_TABLE_ACTION_UPLOAD_DRAGNDROP,
    });
  }, [methods, savedFormDataRef, setAction]);

  const handlePressUpload = useCallback(() => {
    methods.handleSubmit(handleFormSubmit)();
  }, [handleFormSubmit, methods]);

  useEffect(() => {
    const subscription = methods.watch((value) => {
      const rows = value.files;
      if (!rows?.length) {
        return;
      }
      (
        savedFormDataRef as unknown as { current: SavedUploadFormDraft }
      ).current = {
        categories: rows.map((f) => f?.category),
        fileNames: rows.map(
          (f) => `${f?.fileNameStem ?? ""}${f?.fileNameExtension ?? ""}`,
        ),
      };
    });
    return () => subscription.unsubscribe();
  }, [methods]);

  useEffect(() => {
    if (isRemovingRef.current) {
      isRemovingRef.current = false;
      return;
    }
    if (!initialValues.length) {
      lastAppliedInitialValuesSignatureRef.current = null;
      prevSyncedInitialFilesRef.current = [];
      return;
    }

    const prevFiles = prevSyncedInitialFilesRef.current;
    const sameFilesAsLastSync =
      initialValues.length === prevFiles.length &&
      initialValues.every((f, i) => f.file === prevFiles[i]);

    if (
      lastAppliedInitialValuesSignatureRef.current === initialValuesSignature &&
      sameFilesAsLastSync
    ) {
      return;
    }
    lastAppliedInitialValuesSignatureRef.current = initialValuesSignature;

    const fullNames = (savedFormDataRef.current ?? EMPTY_SAVED_UPLOAD_DRAFT)
      .fileNames;
    const existingRows = methods.getValues().files as FileFieldRow[];
    const newFields = initialValues.map((file, index) => {
      const sameInitialFileAtIndex =
        prevFiles[index] !== undefined && prevFiles[index] === file.file;
      const fullName = sameInitialFileAtIndex
        ? fullNames[index] ?? file.fileName ?? ""
        : file.fileName ?? "";
      const existing = existingRows[index];

      return {
        category:
          (savedFormDataRef.current ?? EMPTY_SAVED_UPLOAD_DRAFT).categories[
            index
          ] ?? file.category?.value,
        content: file.content,
        file: file.file,
        fileNameExtension: getFileExtension(fullName),
        fileNameStem: getFileStem(fullName),
        id: existing?.id ?? createClientId(),
        kis_document_id_string: file.kis_document_id_string,
      };
    });

    replace(newFields);

    methods.reset({
      files: newFields,
      should_extract_on_upload: shouldExtractOnUpload,
    });

    prevSyncedInitialFilesRef.current = initialValues.map((f) => f.file);
  }, [initialValuesSignature]);

  if (!initialValues.length) return null;

  return (
    <FormProvider {...methods}>
      <ModalContent>
        <ModalHeader
          as="h1"
          className="border-b border-divider pb-4 text-heading-xl! font-bold"
          id="file-upload-modal-drag-n-drop-title"
        >
          {translations.fileSection.dragAndDrop.title}
        </ModalHeader>
        <ModalBody className="p-6!">
          {isLoading(fileUploadProgress) ? (
            <div className="flex h-full items-center justify-center">
              <Spinner color="primary" size="lg" testId="file-upload-spinner" />
            </div>
          ) : (
            <FileOverview
              fields={fields}
              modalBodyRef={modalBodyRef}
              mutationProgress={mutationProgress}
              onRemoveFile={handleRemoveFileAtIndex}
            />
          )}
        </ModalBody>
        <ModalFooter
          className={clsx("flex w-full flex-col gap-4 p-6!", {
            "relative shadow-2xl!": isScrollable,
          })}
        >
          <ExtractBanner />
          <div className="flex flex-row justify-between gap-4">
            <Button
              color="primary"
              id="file-upload-modal-files-back"
              size="lg"
              startContent={<ArrowLeftIcon />}
              variant="ghost"
              onPress={handlePressBack}
            >
              {translations.actions.back}
            </Button>
            <Button
              color="primary"
              id="file-upload-modal-files-upload"
              size="lg"
              startContent={<UploadIcon />}
              onPress={handlePressUpload}
            >
              {translations.actions.uploadFile}
            </Button>
          </div>
        </ModalFooter>
      </ModalContent>
    </FormProvider>
  );
}
